import { getBaseLayer, getSelectedNode } from "@builder/store/slices/BuilderSlice/builderHelpers";
import { BuilderState } from "@builder/store/slices/BuilderSlice/types";
import { ReceiptLayoutType } from "@builder/utils/constants";
import { DynamicDataKey } from "@common/build/dynamicData";
import { Action, ActionBuilder, CashLedgerNode, Condition, EOSNode, ReceiptNode, TextNode } from "@common/build/types";
import { PayloadAction } from "@reduxjs/toolkit";
import { v4 } from "uuid";

export const updateDynamicDataType = (state: BuilderState, action: PayloadAction<DynamicDataKey | undefined>) => {
    if (state.selectedNode) {
        const selected = getBaseLayer(state).components[state.selectedNode] as TextNode;
        selected.dynamicData = action.payload;

        if (action.payload === undefined) {
            delete selected.prepend;
            delete selected.append;
            delete selected.fallback;
            delete selected.characterLimit;
        }
    }
};

export const updateCondition = (state: BuilderState, action: PayloadAction<Condition>) => {
    if (state.selectedNode) {
        const selectedComponent = getBaseLayer(state).components[state.selectedNode];
        if (selectedComponent.conditions) {
            if (selectedComponent.conditions.length > 0) {
                const index = selectedComponent.conditions.findIndex(condition => condition.id === action.payload.id);
                if (index !== -1) {
                    selectedComponent.conditions[index] = action.payload;
                } else {
                    selectedComponent.conditions.push(action.payload);
                }
            }
        } else {
            selectedComponent.conditions = [];
            selectedComponent.conditions.push(action.payload);
        }
    }
};

export const setCondition = (state: BuilderState, action: PayloadAction<Condition | undefined>) => {
    if (state.selectedNode) {
        const selectedComponent = getBaseLayer(state).components[state.selectedNode];
        if (action.payload === undefined) {
            selectedComponent.conditions = undefined;
        } else {
            selectedComponent.conditions = [action.payload];
        }
    }
};

export const updateConditionalActions = (state: BuilderState, action: PayloadAction<Action>) => {
    if (state.selectedNode) {
        const selectedComponent = getBaseLayer(state).components[state.selectedNode];
        const newAction = action.payload;
        const index = selectedComponent.actions?.findIndex(action => action.id === newAction.id);

        if (index !== undefined && index !== -1) {
            if (selectedComponent.actions) {
                selectedComponent.actions[index] = newAction;
            }
        } else {
            if (selectedComponent.actions) {
                selectedComponent.actions.push(newAction);
            } else {
                selectedComponent.actions = [newAction];
            }
        }
    }
};

export const removeConditionalActions = (state: BuilderState, action: PayloadAction<undefined>) => {
    if (state.selectedNode) {
        const selectedComponent = getBaseLayer(state).components[state.selectedNode];
        selectedComponent.actions = action.payload;
    }
};

export const updateNodeActions = (state: BuilderState, action: PayloadAction<{ actionId?: string; actionPath?: string | null } | undefined>) => {
    if (state.selectedNode) {
        const { actionId, actionPath } = action.payload || {};
        const node = getBaseLayer(state).components[state.selectedNode] as any;
        if (node) {
            if (actionPath) {
                node[actionPath] = actionId ? [actionId] : undefined;
            } else {
                node.actionsBuilder = actionId ? [actionId] : undefined;
            }
        }
    }
};

export const updateBuildActions = (state: BuilderState, action: PayloadAction<string>) => {
    const selectedComponentIds = new Set(state.build.actions);
    const newActionId = action.payload;

    selectedComponentIds.add(newActionId);

    state.build.actions = Array.from(selectedComponentIds);
};

export const updateActionBuilder = (state: BuilderState, action: PayloadAction<ActionBuilder>) => {
    const selectedComponent = state.build;
    if (!selectedComponent.actionBuilder) {
        selectedComponent.actionBuilder = {};
    }
    selectedComponent.actionBuilder[action.payload.id] = action.payload;
};

export const deleteActionBuilder = (state: BuilderState, action: PayloadAction<string>) => {
    const selectedComponent = state.build;
    if (selectedComponent.actionBuilder) {
        delete selectedComponent.actionBuilder[action.payload];
    }

    if (selectedComponent.actions) {
        selectedComponent.actions = selectedComponent.actions.filter(actionItem => actionItem !== action.payload);
    }
};

export const duplicateActionBuilder = (state: BuilderState, action: PayloadAction<string>) => {
    const selectedComponent = state.build;
    if (selectedComponent.actionBuilder && selectedComponent.actionBuilder[action.payload]) {
        const originalActionBuilder = selectedComponent.actionBuilder[action.payload];
        const newId = v4();
        const duplicatedActionBuilder = { ...originalActionBuilder, id: newId };
        selectedComponent.actionBuilder[newId] = duplicatedActionBuilder;
    }
};

export const updateReceiptStyles = (
    state: BuilderState,
    action: PayloadAction<{ type: ReceiptLayoutType | undefined; styles: React.CSSProperties }>
) => {
    if (!action.payload.type) return;

    const selectedNode = getSelectedNode(state) as ReceiptNode;

    if (!selectedNode.styles) {
        selectedNode.styles = {};
    }

    const styleKey = action.payload.type;

    selectedNode.styles[styleKey] = {
        ...selectedNode.styles[styleKey],
        ...action.payload.styles
    };
};

export const updateReceiptSettings = (state: BuilderState, action: PayloadAction<{ type: string; setting: boolean }>) => {
    const selectedNode = getSelectedNode(state) as ReceiptNode;

    selectedNode.settings = {
        ...selectedNode.settings,
        [action.payload.type]: action.payload.setting
    };
};

export const updateCashLedgerSettings = (state: BuilderState, action: PayloadAction<{ setting: boolean }>) => {
    const selectedNode = getSelectedNode(state) as CashLedgerNode;

    selectedNode.settings = { ...selectedNode.settings, showTime: action.payload.setting };
};

export const updateEOSSettings = (state: BuilderState, action: PayloadAction<{ type: string; setting: boolean }>) => {
    const selectedNode = getSelectedNode(state) as EOSNode;

    selectedNode.settings = { ...selectedNode.settings, [action.payload.type]: action.payload.setting };
};

export const updatePaymentGridSettings = (state: BuilderState, action: PayloadAction<{ type: string; setting: boolean }>) => {
    const { type, setting } = action.payload;
    let paymentSettings = state.build.paymentGridSettings;

    if (!paymentSettings) {
        paymentSettings = {
            allowCustom: false,
            allowRename: false
        };
    }

    if (type === "allowCustom") {
        paymentSettings.allowCustom = setting;
    } else if (type === "allowRename") {
        paymentSettings.allowRename = setting;
    }

    state.build.paymentGridSettings = paymentSettings;
};
